import React, { useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { sessionService } from 'redux-react-session'

import { destroy, menu } from '@actions/user'
import { resetTheme } from '@actions/theme'
import exitIcon from '../../Header/images/exit.png'
import { setToken } from '@services/sm-api.js'
import smAuth from '@services/sm-auth.js'

import V2Layout from '../V2Layout'

// Chrome da área da conta no V2 — porte fiel do design "Gestão de Pedidos".
// Regras preservadas (MATRIZ MC-01..MC-04): sidebar montada pelo MESMO selector
// menu() por permissões do V1 (comprador vê Pedidos + Atendimento); "Pedidos" roda
// na V2, os demais itens navegam para as telas V1 (/admin/...). Sair = destroy +
// resetTheme + home, como no AdminMenu.

const sideLink = active => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '11px 12px',
  borderRadius: '8px',
  fontSize: '14.5px',
  fontWeight: active ? '700' : '400',
  color: active ? '#D8262C' : '#6B6664',
  background: active ? '#FDEAEA' : 'transparent',
  textDecoration: 'none',
  cursor: 'pointer',
})

// ícones do menu V1 (pedido de 31/08: facilitam o reconhecimento dos itens)
const menuIcon = src =>
  src ? <img src={src} alt="" style={{ width: '16px', height: '16px', objectFit: 'contain', opacity: 0.75, flex: 'none' }} /> : null

export default function V2AccountLayout({ children }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useSelector(state => (state.sessionReducer && state.sessionReducer.user) || {})
  const menuItems = useSelector(state => menu(state))

  // o boot do axios lê o token de reduxPersist:user, que pode estar ausente/stale
  // enquanto a sessão (redux-react-session) está válida — sincroniza os clientes
  // com o token da sessão ativa (família do achado #17; sem tocar o V1). O conteúdo
  // só renderiza após a sincronização: os componentes V1 espelhados disparam seus
  // fetches no mount e caíam em 500 (JWT null) quando montavam antes do token.
  const [tokenReady, setTokenReady] = useState(false)
  useEffect(() => {
    sessionService
      .loadSession()
      .then(session => {
        if (session && session.token) {
          setToken(session.token)
          smAuth.setToken(session.token)
        }
      })
      .catch(() => {})
      .finally(() => setTokenReady(true))
  }, [])

  const _logout = () => {
    dispatch(destroy())
    dispatch(resetTheme())
    // quem sai do chrome V2 permanece na V2 (reporte de 31/08; o V1 usa '/')
    navigate('/v2')
  }

  const isOrders = url => url === 'pedidos'

  return (
    <V2Layout>
      <div className="v2-acc-grid" style={{ display: 'flex', alignItems: 'stretch', minHeight: '60vh', background: '#f0efec' }}>
        <aside className="v2-acc-side" style={{ flex: 'none', width: '224px', background: '#fff', borderRight: '1px solid #EFE7E3', padding: '26px 0' }}>
          <div className="v2-acc-side__user" style={{ padding: '0 22px 20px', borderBottom: '1px solid #f0efec' }}>
            <p style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '16px', color: '#373535' }}>{user.full_name || user.name}</p>
            <Link id="v2-acc-edit-profile" to={`/v2/conta/perfil/${user.id}`} style={{ fontSize: '12px', color: '#D8262C', textDecoration: 'none' }}>
              Editar perfil
            </Link>
          </div>
          <nav className="v2-acc-side__nav" style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {menuItems.map((item, i) => (
              <React.Fragment key={i}>
                {item.notClickable ? (
                  <span style={{ ...sideLink(false), cursor: 'default', fontWeight: '700', color: '#6B6664' }}>
                    {menuIcon(item.icon)}
                    {item.name}
                  </span>
                ) : (
                  <Link
                    id={isOrders(item.url) ? 'v2-acc-nav-pedidos' : item.url === 'atendimento' ? 'v2-acc-nav-atendimento' : undefined}
                    to={
                      isOrders(item.url)
                        ? '/v2/conta/pedidos'
                        : item.url === 'atendimento'
                          ? '/v2/conta/atendimento'
                          : `/v2/admin/${item.url}`
                    }
                    style={sideLink(
                      (isOrders(item.url) && location.pathname.startsWith('/v2/conta/pedidos')) ||
                        (item.url === 'atendimento' && location.pathname.startsWith('/v2/conta/atendimento')) ||
                        (!isOrders(item.url) && item.url !== 'atendimento' && location.pathname.startsWith(`/v2/admin/${item.url}`))
                    )}
                  >
                    {menuIcon(item.icon)}
                    {item.name}
                  </Link>
                )}
                {(item.items || []).map((sub, j) =>
                  !user.school_id && sub.name === 'Vouchers' ? null : (
                    <Link
                      key={`${i}-${j}`}
                      to={`/v2/admin/${item.url}/${sub.url}`}
                      style={{ ...sideLink(location.pathname.startsWith(`/v2/admin/${item.url}/${sub.url}`)), paddingLeft: '24px' }}
                    >
                      {sub.name}
                    </Link>
                  )
                )}
              </React.Fragment>
            ))}
            <button type="button" id="v2-acc-logout" onClick={_logout} style={{ ...sideLink(false), border: 'none', background: 'none', width: '100%', textAlign: 'left', font: 'inherit' }}>
              {menuIcon(exitIcon)}
              Sair
            </button>
          </nav>
        </aside>

        <main
          style={{ flex: 1, minWidth: 0, padding: location.pathname.startsWith('/v2/admin') ? '28px 24px 56px' : '28px 32px 56px' }}
          className={`v2-acc-main${location.pathname.startsWith('/v2/admin') ? ' v2-admin' : ''}`}
        >
          {/* como pai de rotas (espelho /v2/admin) renderiza o Outlet; como wrapper, children */}
          <div style={{ maxWidth: location.pathname.startsWith('/v2/admin') ? '1280px' : '980px', margin: '0 auto' }}>
            {tokenReady ? children || <Outlet /> : null}
          </div>
        </main>
      </div>
    </V2Layout>
  )
}
